import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FlaskConical, FolderOpen, BookOpen, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  useEffect(() => { api.get("/cases").then((r) => setCases(r.data)).catch(() => {}); }, []);

  return (
    <div className="reveal space-y-10">
      <div>
        <div className="eyebrow">Welcome</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">
          {user?.name?.split(" ")[0]}, your RIF workspace.
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Start a new case, revisit previous plans, or browse the curated research library.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <ActionCard to="/workflow" icon={FlaskConical} title="Start a new RIF case" body="Run the 5-step interactive workflow and generate a dosed protocol." testId="dash-start-case" />
        <ActionCard to="/cases" icon={FolderOpen} title="Patient cases" body={`${cases.length} saved ${cases.length === 1 ? "case" : "cases"} in your history.`} testId="dash-cases" />
        <ActionCard to="/library" icon={BookOpen} title="Research library" body="ESHRE ’23, ASRM, ERA, KIR/HLA, CE, TB references." testId="dash-library" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">Recent cases</h2>
          <Link to="/cases" className="text-sm text-blue-900 font-semibold hover:underline">View all →</Link>
        </div>
        <div className="mt-4 border border-slate-200 rounded-md bg-white divide-y divide-slate-200">
          {cases.length === 0 && (
            <div className="p-6 text-sm text-slate-500">No cases yet. <Link to="/workflow" className="text-blue-900 font-semibold hover:underline">Start your first case →</Link></div>
          )}
          {cases.slice(0, 6).map((c) => (
            <Link key={c.id} to={`/workflow/${c.id}`} className="flex items-center justify-between p-4 hover:bg-slate-50" data-testid={`recent-case-${c.id}`}>
              <div>
                <div className="font-medium text-slate-900">{c.patient_label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionCard({ to, icon: Icon, title, body, testId }) {
  return (
    <Link to={to} className="block border border-slate-200 bg-white rounded-md p-6 hover:border-blue-900 transition-colors" data-testid={testId}>
      <Icon className="h-6 w-6 text-blue-900" strokeWidth={1.5} />
      <div className="mt-4 font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-sm text-slate-600">{body}</div>
      <div className="mt-4 text-sm font-semibold text-blue-900 inline-flex items-center gap-1">
        Open <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
      </div>
    </Link>
  );
}

// Provide a lightweight Button placeholder proxy so import stays lean.
export { Button };
